import React from "react";
import { useTranslation } from "react-i18next";
import { colorVars, formatAvg } from "./contentTeamShared";
import HostAvatar from "./HostAvatar";
import { HostSeason } from "./SeasonHeadToHead";
import styles from "./ContentTeam.module.css";

export interface DuelTeam {
  /** The team's heading, e.g. "OGC-podcasten". */
  name: string;
  predictors: HostSeason[];
}

/** One division: each team's mean score over its hosts with a scored prediction there (null: none). */
export interface DuelResult {
  division: string;
  scores: [number | null, number | null];
}

interface Props {
  season: number;
  teams: [DuelTeam, DuelTeam];
  results: DuelResult[];
  /** People on the season's prediction leaderboard. */
  predictors: number;
}

const mean = (values: number[]): number | null =>
  values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;

/**
 * Compare mode: this podcast's hosts against another podcast's over the whole season. A team's
 * average is the mean of its hosts' season averages; a division goes to the team with the lower
 * mean there. Below, every host of both teams in one ranking.
 */
const PodcastDuel: React.FC<Props> = ({ season, teams, results, predictors }) => {
  const { t } = useTranslation("pages");

  const wins = [0, 0];
  let ties = 0;
  let scored = 0;
  for (const { scores: [a, b] } of results) {
    if (a === null || b === null) continue;
    scored++;
    // On the two decimals the page shows, like the hosts' head-to-head.
    const c = Math.round(a * 100) - Math.round(b * 100);
    if (c === 0) ties++;
    else wins[c < 0 ? 0 : 1]++;
  }

  const avgs = teams.map((team) => mean(team.predictors.map((p) => p.avg).filter((v): v is number => v !== null)));
  const exacts = teams.map((team) => team.predictors.reduce((sum, p) => sum + (p.exact ?? 0), 0));
  const [ra, rb] = avgs.map((v) => (v === null ? null : Math.round(v * 100)));
  const lead = ra === null || rb === null || ra === rb ? null : ra < rb ? 0 : 1;

  let headline = t("contentTeam.waiting");
  let line1 = "";
  let line2 = "";
  if (scored > 0) {
    if (lead === null) {
      headline = t("contentTeam.even");
      line1 = t("contentTeam.sameAvg");
    } else {
      headline = t("contentTeam.leads", { name: teams[lead].name });
      line1 = t("contentTeam.leadBy", { diff: (Math.abs((ra ?? 0) - (rb ?? 0)) / 100).toFixed(2) });
    }
    line2 = t("contentTeam.divisionsEven", { a: wins[0], b: wins[1] });
    if (ties > 0) line2 += t("contentTeam.ties", { count: ties });
  }

  const side = (team: DuelTeam, index: number) => (
    <div className={`${styles.side} ${index === 1 ? styles.sideRight : ""}`} style={colorVars(team.predictors[0].color)}>
      <span className={styles.duo}>
        {team.predictors.map((p) => (
          <HostAvatar key={p.steamId} steamId={p.steamId} name={p.name} color={p.color} size="md" />
        ))}
      </span>
      <div className={styles.sideText}>
        <span className={styles.sideName}>
          <span className={styles.sideNameText}>{team.name}</span>
        </span>
        <span className={styles.sideStats}>
          <span className={styles.stat}>
            <span className={styles.statValue}>{scored > 0 ? formatAvg(avgs[index]) : "–"}</span>
            <span className={styles.statLabel}>{t("contentTeam.avg")}</span>
          </span>
          <span className={styles.stat}>
            <span className={styles.statValue}>{scored > 0 ? exacts[index] : "–"}</span>
            <span className={styles.statLabel}>{t("contentTeam.exact")}</span>
          </span>
          <span className={styles.stat}>
            <span className={`${styles.statValue} ${styles.statValueAccent}`}>{scored > 0 ? wins[index] : "–"}</span>
            <span className={styles.statLabel}>{t("contentTeam.divisionsWon")}</span>
          </span>
        </span>
      </div>
    </div>
  );

  const ranking = teams
    .flatMap((team) => team.predictors.map((p) => ({ p, team: team.name })))
    .sort((x, y) => (x.p.avg ?? Infinity) - (y.p.avg ?? Infinity));

  return (
    <section className={styles.panel} aria-label={t("contentTeam.duelTitle")}>
      <div className={styles.strip}>
        <h2 className={styles.stripTitle}>{t("contentTeam.duelTitle")}</h2>
        <span className={styles.muted}>{t("contentTeam.duelSub", { season })}</span>
      </div>
      <div className={styles.h2hBody}>
        {side(teams[0], 0)}
        <div className={styles.center} style={lead !== null ? colorVars(teams[lead].predictors[0].color) : undefined}>
          <span className={styles.centerLabel}>{t("contentTeam.wholeSeason")}</span>
          <span className={styles.headline}>{headline}</span>
          {line1 && <span className={styles.muted}>{line1}</span>}
          {line2 && <span className={styles.muted}>{line2}</span>}
          {scored > 0 && <span className={styles.centerRule}>{t("contentTeam.duelRule")}</span>}
        </div>
        {side(teams[1], 1)}
      </div>
      <div className={styles.duelList}>
        <div className={`${styles.duelRow} ${styles.duelHead}`}>
          <span />
          <span className={styles.sectionLabel}>{t("contentTeam.duelRanking")}</span>
          <span className={styles.duelNum}>{t("contentTeam.avg")}</span>
          <span className={styles.duelNum}>{t("contentTeam.exact")}</span>
        </div>
        {ranking.map(({ p, team }, i) => (
          <div key={p.steamId} className={styles.duelRow} style={colorVars(p.color)}>
            <span className={styles.duelPlace}>{scored > 0 && p.avg !== null ? i + 1 : "–"}</span>
            <span className={styles.duelWho}>
              <HostAvatar steamId={p.steamId} name={p.name} color={p.color} size="sm" />
              <span className={styles.duelText}>
                <span className={styles.duelName}>{p.name}</span>
                <span className={styles.duelTeam}>{team}</span>
              </span>
              {p.rank !== null && (
                <span className={`${styles.muted} ${styles.duelLb}`}>{t("contentTeam.lbRank", { rank: p.rank, total: predictors })}</span>
              )}
            </span>
            <span className={`${styles.duelNum} ${styles.duelValue}`}>{scored > 0 ? formatAvg(p.avg) : "–"}</span>
            <span className={`${styles.duelNum} ${styles.duelValue}`}>{scored > 0 && p.exact !== null ? p.exact : "–"}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PodcastDuel;
